import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
  getAvatarColor,
} from '@heidi/design-system/avatar';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { AssigneeType } from '@heidi/schema/ml-scribe';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import {
  getInitialsFromFullName,
  getTeamInitial,
} from './clinical-task-assignee-list-items.utils';

interface ClinicalTaskAssigneeTriggerIconProps {
  assigneeId?: string | null;
  assigneeName?: string | null;
  assigneeAvatarUrl?: string | null;
  assigneeType?: AssigneeType;
  emptyIcon: ReactNode;
  withHeidi?: boolean;
}

// The leading icon for the picker's trigger chip, kept separate from the Chip
// itself so Chip stays the direct asChild child Radix's DropdownMenuTrigger clones.
export function ClinicalTaskAssigneeTriggerIcon({
  assigneeId,
  assigneeName,
  assigneeAvatarUrl,
  assigneeType,
  emptyIcon,
  withHeidi = false,
}: ClinicalTaskAssigneeTriggerIconProps) {
  const intl = useIntl();
  const heidiLabel = intl.formatMessage({
    defaultMessage: 'Heidi',
    description: 'Accessible name for the Heidi status mark on the owner chip',
    id: 'MjlGcmUJEv',
  });

  if (!assigneeId) {
    if (!withHeidi) return emptyIcon;

    return (
      <Avatar size="xs" color="neutral" className="shrink-0">
        <AvatarFallback
          icon={
            <HeidiLogoMark className="size-3 text-current" data-testid="heidi-mark" />
          }
        />
        <span className="sr-only">{heidiLabel}</span>
      </Avatar>
    );
  }

  // AvatarGroup paints later siblings on top; z-index keeps the owner in front.
  const ownerClassName = withHeidi ? 'relative z-[1] shrink-0' : 'shrink-0';

  const ownerAvatar =
    assigneeType === AssigneeType.team ? (
      <Avatar
        color={getAvatarColor(assigneeId)}
        size="xs"
        shape="square"
        className={ownerClassName}
      >
        <AvatarFallback>{getTeamInitial(assigneeName ?? '')}</AvatarFallback>
      </Avatar>
    ) : (
      <Avatar
        size="xs"
        color={getAvatarColor(assigneeName ?? '')}
        className={ownerClassName}
      >
        <AvatarImage src={assigneeAvatarUrl ?? undefined} alt={assigneeName ?? ''} />
        <AvatarFallback>{getInitialsFromFullName(assigneeName)}</AvatarFallback>
      </Avatar>
    );

  if (!withHeidi) return ownerAvatar;

  return (
    <AvatarGroup className="shrink-0">
      {ownerAvatar}
      <Avatar size="xs" color="neutral">
        <AvatarFallback
          icon={
            <HeidiLogoMark className="size-3 text-current" data-testid="heidi-mark" />
          }
        />
        <span className="sr-only">{heidiLabel}</span>
      </Avatar>
    </AvatarGroup>
  );
}
